/**
 * RepeatRateCard — بطاقة «معدل تكرار العملاء».
 *
 * معدل التكرار = عدد الفواتير ÷ عدد العملاء المتمايزين، لفترة/فرع مختارَين.
 * تقرأ حصراً من GET /api/customers/repeat-rate (يمرّ عبر core.pos_data، Rule #1).
 * تُعيد استخدام نفس فلتر الفرع (BranchSelect) ونفس منتقي التاريخ المعياري
 * (DateField) المستعمَلَين في بطاقة العملاء الجدد — لا نسخة ثالثة.
 * عدّاد «هامش الفقد» = نفس منطق عدّاد فقد الهوية الكامل في الخادم (لا تعريف جديد).
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { api } from "../lib/api";
import { useCustomerFilter } from "./CustomerFilterContext";

const TEAL = "#065F46";
const TEAL_SOFT = "#0F766E";
const BORDER = "1px solid rgba(6,95,70,0.20)";

const _lbl = { fontSize: 10, color: TEAL_SOFT, fontWeight: 700 };
const _val = { marginTop: 2, fontWeight: 600, fontSize: 12, color: TEAL };
const _input = {
  fontSize: 13, padding: "6px 10px", borderRadius: 10,
  border: BORDER, background: "#fff", color: TEAL, fontWeight: 600,
};

export const RepeatRateCard = () => {
  // Phase 7h — تستهلك فلتر الشريط الموحّد حصريًا (حُذفت أدوات التحكم
  // المحلية والتجاوز المحلي نهائيًا). لا مصدر تاريخ/فرع بديل.
  const shared = useCustomerFilter();
  const start = shared?.from || "";
  const end = shared?.to || "";
  const branchEff = shared?.branchId || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const reqId = React.useRef(0);

  const load = useCallback(() => {
    const myId = ++reqId.current;
    setLoading(true);
    setErr(false);
    return api
      .get("/customers/repeat-rate", {
        params: { start, end, branch_id: branchEff || undefined },
        timeoutClass: "REPORT",
      })
      .then((r) => { if (myId === reqId.current) setData(r.data); })
      .catch(() => { if (myId === reqId.current) setErr(true); })
      .finally(() => { if (myId === reqId.current) setLoading(false); });
  }, [start, end, branchEff]);

  useEffect(() => { load(); }, [load]);

  const branches = data?.branches || [];
  const ratio = data?.repeat_rate ?? 0;
  const numerator = data?.numerator_invoices ?? 0;
  const customers = data?.distinct_customers ?? 0;
  const loss = data?.full_identity_loss ?? 0;
  const lossPct = data?.full_identity_loss_pct ?? 0;
  const invoicesTotal = data?.invoices_total ?? 0;

  return (
    <section
      data-testid="repeat-rate-card"
      dir="rtl"
      style={{
        marginTop: 24, padding: "18px 20px", borderRadius: 16,
        background: "#fff", border: BORDER,
        boxShadow: "0 1px 3px rgba(6,95,70,0.06)",
        fontVariantNumeric: "lining-nums tabular-nums",
      }}
    >
      {/* ── العنوان */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: TEAL }}>
          🔁 معدل تكرار العملاء
        </h3>
        <span style={{ fontSize: 12, color: TEAL_SOFT }}>
          عدد الفواتير ÷ عدد العملاء المتمايزين خلال الفترة المختارة
        </span>
      </div>

      {/* ── الرقم الرئيسي + هامش الفقد */}
      {err ? (
        <div style={{ marginTop: 18, color: "#B91C1C", fontSize: 13 }}>
          تعذّر تحميل البيانات — أعد المحاولة
          <button data-testid="repeat-rate-retry" onClick={load}
            style={{ ..._input, marginInlineStart: 10, cursor: "pointer" }}>
            إعادة المحاولة
          </button>
        </div>
      ) : (
        <div style={{
          marginTop: 18, display: "flex", gap: 28, flexWrap: "wrap",
          alignItems: "flex-start",
        }}>
          {/* الرقم البسيط */}
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span data-testid="repeat-rate-value" style={{
                fontSize: 40, fontWeight: 900, color: TEAL, lineHeight: 1,
                fontFamily: "monospace",
              }}>
                {loading ? "…" : ratio.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span style={{ fontSize: 13, color: TEAL_SOFT, fontWeight: 600 }}>
                فاتورة لكل عميل
              </span>
            </div>
            <div data-testid="repeat-rate-formula" style={{
              marginTop: 8, fontSize: 13, color: TEAL_SOFT, fontWeight: 600,
              fontFamily: "monospace",
            }}>
              {numerator.toLocaleString("en-US")} فاتورة ÷{" "}
              {customers.toLocaleString("en-US")} عميل ={" "}
              {ratio.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>

          {/* عدّاد هامش الفقد */}
          <div style={{
            padding: "12px 16px", borderRadius: 14,
            background: "linear-gradient(to left, #FEF3F2, #FFF7ED)",
            border: "1px solid rgba(185,28,28,0.20)", minWidth: 200,
          }}>
            <div style={{ fontSize: 10, color: "#B45309", fontWeight: 700 }}>
              هامش الفقد (بلا اسم وبلا هاتف)
            </div>
            <div data-testid="repeat-rate-loss" style={{
              marginTop: 4, fontSize: 15, fontWeight: 800, color: "#B91C1C",
            }}>
              {loss.toLocaleString("en-US")} فاتورة ·{" "}
              {lossPct.toLocaleString("en-US")}% هامش فقد
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: "#B45309" }}>
              مستبعَدة من بسط المعدل — من إجمالي{" "}
              {invoicesTotal.toLocaleString("en-US")} فاتورة في الفترة
            </div>
          </div>
        </div>
      )}

      {/* ── رسم بياني: منحنى معدل التكرار اليومي عبر أيام الفترة المختارة */}
      {!err && (
        <div data-testid="repeat-rate-chart" style={{ marginTop: 18, height: 240, width: "100%" }}>
        {loading ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: TEAL_SOFT, fontSize: 13 }}>
            …جارٍ التحميل
          </div>
        ) : (data?.series || []).length === 0 ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#78716C", fontSize: 13 }}>
            لا توجد بيانات يومية للفترة المختارة
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={(data.series || []).map((s) => ({ period: s.period, repeat_rate: s.repeat_rate, partial: !!s.partial }))}
                       margin={{ top: 8, right: 12, left: -8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(6,95,70,0.12)" />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#78716C" }}
                     tickFormatter={(v) => String(v).slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#78716C" }} allowDecimals />
              <Tooltip
                formatter={(v) => [`${v}`, "معدل التكرار"]}
                labelFormatter={(v, payload) => {
                  const isPartial = payload?.[0]?.payload?.partial;
                  return isPartial ? `${v} (جزئي — اليوم الجاري)` : v;
                }}
                labelStyle={{ fontFamily: "monospace" }}
              />
              <Line type="monotone" dataKey="repeat_rate" stroke={TEAL} strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload, index } = props;
                      // ⑥/إنتاج (2026-11-04) — اليوم الجاري (بيانات جزئية حتى لحظة
                      // الطلب، لا يوم مكتمل) يُعلَّم بنقطة مفرَّغة متقطّعة، تمييزاً
                      // عن الأيام المكتملة — لا تُعرض أيام مستقبلية إطلاقاً (مصدرها
                      // الباك-إند مقطوع عند اليوم التشغيلي الحالي).
                      if (payload?.partial) {
                        return (
                          <circle key={`dot-${index}`} cx={cx} cy={cy} r={4} fill="#fff"
                                  stroke={TEAL} strokeWidth={2} strokeDasharray="2,2" />
                        );
                      }
                      return <circle key={`dot-${index}`} cx={cx} cy={cy} r={3} fill={TEAL} />;
                    }}
                    activeDot={{ r: 5 }} name="معدل التكرار" />
            </LineChart>
          </ResponsiveContainer>
        )}
        </div>
      )}
      {!err && (data?.series || []).some((s) => s.partial) && (
        <div data-testid="repeat-rate-partial-note" style={{ marginTop: 6, fontSize: 11, color: "#78716C" }}>
          ⚬ اليوم الجاري جزئي (بيانات حتى لحظة الطلب، لا يوم مكتمل)
        </div>
      )}

      {/* ── شريط التصريح: المصدر + النافذة + الفرع + هامش الفقد */}
      <section
        data-testid="repeat-rate-conditions"
        style={{
          marginTop: 16, padding: "12px 16px", borderRadius: 14,
          background: "linear-gradient(to left, #F0F9FF, #ECFDF5)",
          border: BORDER, display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 12,
        }}
      >
        <div>
          <div style={_lbl}>المصدر</div>
          <div data-testid="repeat-rate-source" style={{ ..._val, fontFamily: "monospace", fontSize: 11 }}>
            pos_sales · عبر core.pos_data
          </div>
        </div>
        <div>
          <div style={_lbl}>نافذة القياس</div>
          <div data-testid="repeat-rate-window" style={_val}>
            {data?.start} ← {data?.end}
          </div>
        </div>
        <div>
          <div style={_lbl}>الفرع المختار</div>
          <div data-testid="repeat-rate-branch-label" style={_val}>
            {data?.branch_id
              ? (branches.find((b) => b.id === data.branch_id)?.name || data.branch_id)
              : "كل الفروع"}
          </div>
        </div>
        <div>
          <div style={_lbl}>هامش الفقد (مستبعَد من البسط)</div>
          <div data-testid="repeat-rate-loss-note" style={_val}>
            {loss.toLocaleString("en-US")} فاتورة · {lossPct.toLocaleString("en-US")}%
          </div>
        </div>
        <div>
          <div style={_lbl}>البسط</div>
          <div style={_val}>فواتير الفترة/الفرع بلا فواتير فقد الهوية الكامل</div>
        </div>
        <div>
          <div style={_lbl}>المقام</div>
          <div style={_val}>عملاء متمايزون بعد normalize_phone</div>
        </div>
      </section>
    </section>
  );
};

export default RepeatRateCard;
